import * as React from 'react';
import { Add } from '../../Icon/icon';
import './pageFields.scss';
import { useActions } from '../schemaModalActions';
import { cn, dndMove, getSchema, isAuditType } from '../../../utils';
import { ModalGroup, ModalGroups } from '../../ModalGroup/modalGroup';
import { Icon } from 'rcd-cpp-ui/ui/icon';
import { FieldCard } from '../FieldCard/fieldCard';
import { NodeType } from '../../../enums/NodeType';
import { findOne } from 'rcd-cpp-workspace/utils/array';
import { DataType } from '../../../enums/DataType';
import { Dropdown } from '../../Dropdown/dropdown';
import {
  ThreeDots,
  TrashSmallGrey,
  AddAbove,
  AddBelow,
  MoveTo,
  MoveToTop,
  MoveToBottom
} from '../../Icon/icon';
import { useConfirmModal } from '../../ConfirmModal/confirmModal';
import { Cancel, Primary, Buttons } from 'rcd-cpp-ui/ui/button';
import { Modal } from 'rcd-cpp-ui/ui/modal/Modal';
import { useAlert } from '../../Alert/alert';
import { useSelector } from '../../../utils/useSelector';

export const hasShowHideOption = (nodeType: NodeType) =>
  nodeType == NodeType.Item;

export const PageFields = () => {
  const nodeType = useSelector(s => s.schemaModal?.nodeType);
  const fields = useSelector(s => s.schemaModal?.fields);
  const selected = useSelector(s => s.schemaModal?.selectedField);
  const schemas = useSelector(s => s.planning?.selectedWorkspace?.schemas);
  const additionalInfo = useSelector(s => s.schemaModal?.additionalInfo);
  const workspace = useSelector(s => s.planning?.selectedWorkspace);
  const { addField, selectField, updateFields } = useActions();
  const ref = React.useRef(null);
  React.useEffect(() => {
    ref?.current?.scrollIntoView();
  }, [ref, selected]);
  const existingFields = fields?.filter(f => !!f.key).map(f => f.field);
  const schema = getSchema(schemas, nodeType);
  const orderField = findOne(fields, { isOrderField: true });
  const displayField = findOne(fields, { field: schema?.props?.displayField });
  const dataTypes = schemas
    ?.map(s => ({
      dataType: s.props.type,
      name: s.props.name
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter(dt => !(dt.dataType == NodeType.Comment));
  const fieldsToDisplay = fields?.filter(f => !f.parentField);
  let canAddField = true;
  if (
    schema?.isInherited &&
    workspace.parent &&
    !workspace.props?.allowOwnCustomData
  ) {
    canAddField = false;
  }
  return (
    <ModalGroups id="schemaFields">
      <ModalGroup
        label={
          <>
            Field List
            {canAddField && (
              <div className="add-more">
                <Icon onClick={addField}>
                  <Add /> Add
                </Icon>
              </div>
            )}
          </>
        }
      >
        <div>
          <div>
            <div>
              <div>Field Name</div>
              <div>Type</div>
              <div>Required to Enter</div>
            </div>
            <div></div>
          </div>
        </div>
        <div className="scrollable">
          {fieldsToDisplay.map((f, index) => {
            const isDisabled =
              f.isInherited ||
              f.isSystemField ||
              f.field == 'isActive' ||
              isAuditType(f.relationType);

            let isSelected = selected == f.field;
            const canConvertType =
              f.dataType == DataType.string ||
              f.dataType == DataType.longString;
            let isError = false;
            let errorDisplayMessage = '';
            if (Array.isArray(additionalInfo))
              additionalInfo?.forEach(e => {
                if (e.field == f.field) {
                  isError = true;
                  errorDisplayMessage = e.message;
                }
              });
            const canRemoveField =
              !isDisabled && f.field != displayField?.field;

            return (
              <React.Fragment key={f.field}>
                <div
                  ref={isSelected ? ref : null}
                  className={cn({
                    'selected-field': isSelected,
                    'disabled-field': isDisabled,
                    'error-field': isError
                  })}
                >
                  <FieldCard
                    field={f}
                    key={f.key}
                    displayField={displayField}
                    orderField={orderField}
                    nodeType={nodeType}
                    disabled={isDisabled}
                    onClick={() => selectField(f.field)}
                    onMove={dndMove(fieldsToDisplay, updateFields)}
                    dataTypes={dataTypes}
                    index={index}
                    existingField={existingFields?.includes(f.field)}
                    disableType={
                      existingFields?.includes(f.field) && !canConvertType
                    }
                  />
                  <div>
                    <FieldActionsMenu
                      canRemoveField={canRemoveField}
                      field={f}
                      index={index}
                      fieldsToDisplay={fieldsToDisplay}
                    />
                  </div>
                </div>
                {isError && (
                  <div className="field-error">{errorDisplayMessage}</div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </ModalGroup>
    </ModalGroups>
  );
};

const FieldActionsMenu = ({
  canRemoveField,
  field,
  index,
  fieldsToDisplay
}) => {
  enum Action {
    AddAbove = 'Add Above',
    AddBelow = 'Add Below',
    MoveTo = 'Move To...',
    MoveToTop = 'Move To Top',
    MoveToBottom = 'Move To Bottom',
    Delete = 'Delete'
  }
  const viewName = useSelector(s => s.schemaModal?.name);
  const { removeField, updateFields, addItemField } = useActions();
  const moveField = dndMove(fieldsToDisplay, updateFields);
  const [showMoveModal, setShowMoveModal] = React.useState(false);
  const { confirm, ConfirmModal: DeleteModal } = useConfirmModal({
    label: 'Delete'
  });

  const handleSelect = (action: Action) => {
    switch (action) {
      case Action.AddAbove:
        addItemField(index, 'above', fieldsToDisplay);
        return;
      case Action.AddBelow:
        addItemField(index, 'below', fieldsToDisplay);
        return;
      case Action.MoveTo:
        setShowMoveModal(true);
        return;
      case Action.MoveToTop:
        moveField(index, 0);
        return;
      case Action.MoveToBottom:
        moveField(index, fieldsToDisplay.length);
        return;
      case Action.Delete:
        if (canRemoveField) {
          confirm(
            <>
              This will remove the field
              {viewName ? ` from the ${viewName} form` : ''}. Are you sure you
              want to delete the&nbsp;
              <b>{field.name}</b> field?
            </>,
            {
              header: 'Delete Field',
              onDelete: () => {
                return removeField(field.field);
              }
            }
          );
        }
    }
  };

  return (
    <div className="field-actions-menu-container">
      <div className="delete-modal-wrapper">
        <DeleteModal />
      </div>
      <Dropdown
        position="right"
        className="field-actions-menu"
        options={[
          Action.AddAbove,
          Action.AddBelow,
          Action.MoveTo,
          Action.MoveToTop,
          Action.MoveToBottom,
          Action.Delete
        ]}
        disabled={action => {
          if (action == Action.Delete && !canRemoveField) return true;
          if (action == Action.MoveToTop && index == 0) return true;
          if (
            action == Action.MoveToBottom &&
            index == fieldsToDisplay.length - 1
          )
            return true;
          if (action == Action.MoveTo && fieldsToDisplay.length < 2)
            return true;
        }}
        onSelect={handleSelect}
        renderIcon={action => {
          switch (action) {
            case Action.AddAbove:
              return <AddAbove />;
            case Action.AddBelow:
              return <AddBelow />;
            case Action.MoveTo:
              return <MoveTo />;
            case Action.MoveToTop:
              return <MoveToTop />;
            case Action.MoveToBottom:
              return <MoveToBottom />;
            case Action.Delete:
              return <TrashSmallGrey />;
          }
        }}
      >
        <div className="three-dots">
          <ThreeDots />
        </div>
      </Dropdown>
      {showMoveModal && (
        <MoveModal
          setShowMoveModal={setShowMoveModal}
          index={index}
          fieldsToDisplay={fieldsToDisplay}
        />
      )}
    </div>
  );
};

const MoveModal = ({ setShowMoveModal, index, fieldsToDisplay }) => {
  const [sequence, setSequence] = React.useState(null);
  const [fieldDropdownValue, setFieldDropdownValue] = React.useState(null);
  const { updateFields } = useActions();
  const { Alert, setMessage } = useAlert({});
  const moveField = dndMove(fieldsToDisplay, updateFields);
  const handleMove = () => {
    if (!sequence && (!fieldDropdownValue || fieldDropdownValue == 'select')) {
      setMessage('Please select a sequence and field option');
    } else if (!sequence) {
      setMessage('Please select a sequence option');
    } else if (!fieldDropdownValue || fieldDropdownValue == 'select') {
      setMessage('Please select a field option');
    } else {
      if (sequence == 'above') {
        moveField(index, fieldDropdownValue - 1);
      } else if (sequence == 'below') {
        moveField(index, fieldDropdownValue);
      }
      setShowMoveModal(false);
    }
  };

  return (
    <div className="move-modal-wrapper">
      <Modal
        id="moveModal"
        header="Move To"
        onClose={() => setShowMoveModal(false)}
        footer={
          <Buttons>
            <Cancel onClick={() => setShowMoveModal(false)}></Cancel>
            <Primary label="Move" onClick={() => handleMove()}></Primary>
          </Buttons>
        }
      >
        <Alert />
        <label>Select Sequence</label>
        <div className="move-radio-group">
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name="sequence"
              value="above"
              onChange={e => setSequence(e.target.value)}
            ></input>
            <label className="form-check-label">Above</label>
          </div>
          <div className="form-check form-check-inline">
            <input
              className="form-check-input"
              type="radio"
              name="sequence"
              value="below"
              onChange={e => setSequence(e.target.value)}
            ></input>
            <label className="form-check-label">Below</label>
          </div>
        </div>
        <label>Select Field</label>
        <div className="move-dropdown-group">
          <select
            className="form-control"
            onChange={e => setFieldDropdownValue(e.target.selectedIndex)}
          >
            <option value="select">Select...</option>
            {fieldsToDisplay.map(({ field, name }, currIndex) => {
              if (currIndex != index) {
                return (
                  <option key={field} value={field}>
                    {name}
                  </option>
                );
              }
              return null;
            })}
          </select>
        </div>
      </Modal>
    </div>
  );
};
